import { useEffect, useRef, useState } from "react";
import { AcademicCapIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { useCurrentUser, userIsRealAppAdmin } from "@/lib/auth";
import { useTour } from "@/features/tours/TourProvider";

export function TourMenu() {
  const { user } = useCurrentUser();
  const { tours, loading, active, start, stop } = useTour();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      const node = event.target;
      if (!(node instanceof Node) || !rootRef.current?.contains(node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!userIsRealAppAdmin(user)) return null;
  if (!loading && tours.length === 0) return null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        className={clsx(
          "inline-flex h-9 items-center gap-1.5 rounded-full px-2.5 text-sm font-medium text-label transition hover:bg-fill",
          active && "bg-accent/15 text-accent",
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Anleitungen"
        onClick={() => setOpen((value) => !value)}
      >
        <AcademicCapIcon className="h-5 w-5" aria-hidden />
        <span className="hidden lg:inline">Anleitungen</span>
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label="Anleitungen"
          className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-ios-lg bg-card shadow-ios-md ring-1 ring-separator/40"
        >
          <div className="border-b border-separator px-4 py-3">
            <p className="text-sm font-semibold text-label">Anleitungen</p>
            <p className="mt-0.5 text-xs text-label-secondary">
              Nur Aufgaben, die du mit deinen Rechten ausführen kannst.
            </p>
          </div>
          <ul className="max-h-80 overflow-y-auto py-1">
            {loading ? (
              <li className="px-4 py-3 text-sm text-label-secondary">Anleitungen werden geladen …</li>
            ) : (
              tours.map((tour) => (
                <li key={tour.id}>
                  <button
                    type="button"
                    className="block w-full px-4 py-2.5 text-left hover:bg-fill-tertiary"
                    onClick={() => {
                      setOpen(false);
                      start(tour.id);
                    }}
                  >
                    <span className="block text-sm font-medium text-label">{tour.title}</span>
                    <span className="mt-0.5 block text-xs text-label-secondary">{tour.summary}</span>
                  </button>
                </li>
              ))
            )}
          </ul>
          {active ? (
            <div className="border-t border-separator px-4 py-2">
              <button
                type="button"
                className="text-sm font-medium text-accent"
                onClick={() => {
                  stop();
                  setOpen(false);
                }}
              >
                Führung beenden
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
